"use client";

import { useEffect, useState } from "react";
import {
  getSupplierPerfSnapshot,
  isSupplierPerfEnabled,
} from "./supplierPerfDiagnostics";

const SHOW_OVERLAY =
  typeof process !== "undefined" &&
  process.env.NODE_ENV === "development" &&
  process.env.NEXT_PUBLIC_SUPPLIER_PERF_OVERLAY !== "0";

/**
 * Phase 5 — optional dev-only floating perf panel (no production UI).
 */
export default function SupplierPerfDebugOverlay() {
  const [visible, setVisible] = useState(false);
  const [snap, setSnap] = useState({ api: [], marks: [] });

  useEffect(() => {
    if (!SHOW_OVERLAY || !isSupplierPerfEnabled()) return undefined;

    const refresh = () => setSnap(getSupplierPerfSnapshot());
    refresh();

    const onApi = () => refresh();
    const onMark = () => refresh();
    window.addEventListener("supplier_perf_api", onApi);
    window.addEventListener("supplier_perf_mark", onMark);

    const onKey = (e) => {
      if (e.altKey && e.shiftKey && e.key.toLowerCase() === "p") {
        setVisible((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("supplier_perf_api", onApi);
      window.removeEventListener("supplier_perf_mark", onMark);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  if (!SHOW_OVERLAY || !isSupplierPerfEnabled() || !visible) return null;

  const recentApi = snap.api.slice(-6).reverse();
  const recentMarks = snap.marks.slice(-4).reverse();

  return (
    <div
      className="fixed bottom-3 left-3 z-[99999] max-w-md rounded-lg border border-gray-300 bg-white/95 p-3 text-[11px] shadow-lg backdrop-blur-sm font-mono"
      aria-label="Supplier performance debug overlay"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="font-semibold text-gray-800">Supplier perf (dev)</span>
        <button
          type="button"
          className="text-gray-500 hover:text-gray-800"
          onClick={() => setVisible(false)}
        >
          hide
        </button>
      </div>
      <p className="mb-2 text-gray-500">Alt+Shift+P · console: __supplierPerf.dump()</p>
      {recentApi.length === 0 ? (
        <p className="text-gray-500">No API timings yet</p>
      ) : (
        <ul className="mb-2 space-y-1">
          {recentApi.map((e, i) => (
            <li
              key={`${e.at}-${i}`}
              className={
                e.level === "very_slow"
                  ? "text-red-600"
                  : e.level === "slow"
                    ? "text-amber-700"
                    : "text-gray-700"
              }
            >
              {e.durationMs}ms {e.method} {e.status}{" "}
              {e.url.replace(/^https?:\/\/[^/]+/, "")}
            </li>
          ))}
        </ul>
      )}
      {recentMarks.length > 0 && (
        <ul className="space-y-1 border-t border-gray-200 pt-2 text-gray-600">
          {recentMarks.map((m, i) => (
            <li key={`${m.at}-${i}`}>
              {m.kind}: {m.name}
              {m.durationMs != null ? ` (${m.durationMs}ms)` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
